import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { adminConditionsAPI } from '../services/api';

const emptyForm = {
  display_name: '',
  display_name_ar: '',
  description: '',
  description_ar: '',
  sort_order: '0',
  is_active: true,
  background_color: '#f3f3f5',
};

const ConditionFormPage = () => {
  const { conditionId } = useParams();
  const navigate = useNavigate();
  const [internalName, setInternalName] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await adminConditionsAPI.getAll();
        const list = response.data?.conditions || [];
        const c = list.find((x) => String(x.id) === String(conditionId));
        if (!cancelled) {
          if (c) {
            setInternalName(c.name || '');
            setFormData({
              display_name: c.display_name || '',
              display_name_ar: c.display_name_ar || '',
              description: c.description || '',
              description_ar: c.description_ar || '',
              sort_order: String(c.sort_order ?? 0),
              is_active: Boolean(c.is_active),
              background_color: c.background_color || '#f3f3f5',
            });
          } else {
            toast.error('Condition not found');
          }
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conditionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.display_name?.trim()) {
      toast.error('Display name is required');
      return;
    }
    try {
      await adminConditionsAPI.update(conditionId, {
        display_name: formData.display_name.trim(),
        display_name_ar: formData.display_name_ar?.trim() || null,
        description: formData.description?.trim() || null,
        description_ar: formData.description_ar?.trim() || null,
        sort_order: parseInt(formData.sort_order, 10) || 0,
        is_active: formData.is_active,
        background_color: formData.background_color || null,
      });
      toast.success('Condition updated');
      navigate('/conditions');
    } catch (error) {
      let msg = 'Error updating condition: ';
      if (error.response?.data?.errors) {
        msg += Object.values(error.response.data.errors).flat().join('\n');
      } else {
        msg += error.response?.data?.message || error.message;
      }
      toast.error(msg);
    }
  };

  return (
    <AdminSubpage title="Edit condition" backTo="/conditions">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Internal key</label>
              <input type="text" value={internalName} disabled />
            </div>
            <div className="form-group">
              <label>Display name *</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Display name (Arabic)</label>
              <input
                type="text"
                value={formData.display_name_ar}
                onChange={(e) => setFormData({ ...formData, display_name_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description (Arabic)</label>
              <textarea
                rows={3}
                value={formData.description_ar}
                onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Badge background color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="color"
                  value={formData.background_color || '#f3f3f5'}
                  onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  style={{ width: 48, height: 36, padding: 0, border: 'none' }}
                />
                <input
                  type="text"
                  value={formData.background_color}
                  onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  placeholder="#E8F5E9"
                  style={{ flex: 1 }}
                />
                <span
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    backgroundColor: formData.background_color || '#f3f3f5',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {formData.display_name || 'Preview'}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>Sort order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />{' '}
                Active
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/conditions')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default ConditionFormPage;
